// Keeps connections from being drawn on top of each other.
//
// A forward connection is drawn as: a flat stub out of the parent, a vertical drop, a flat run into
// the child's arrow. The x of that drop comes from the CHILD alone (`child.x - 29.5` for a task,
// `parent.x + 130` for a gateway), so every line into the same column drops at the SAME x:
//
//    column 0                       column 1
//                        x=210.5 -- both lines pick this x
//   +--------+               |
//   |   e6   |---------------+---+
//   +--------+               |   |      +--------+
//                            |   +----->|   e8   |
//   +--------+               |          +--------+
//   |   e5   |---------------+---+
//   +--------+               |   |      +--------+
//                            |   +----->|   e7   |
//                            |          +--------+
//
// Where two of them also cover the same rows, they are painted as one line and you cannot tell which
// parent leads to which child. This module spreads those apart. It returns, per connection, how many
// pixels sideways its vertical should move. The drawing stays in svgPathHelpers.
//
// A loop-back climbs into its child on that same x, so it shares the channel and is handled here too.
//
// TERMS
//
//   baseX     one per connection: the x it drops at, before anything moves it
//   channel   one per column: the connections dropping in that column. Only these can collide, so
//             the grouping is a filter - connections in different channels are never compared.
//   trunk     connections that must share a track: the forward ones out of ONE parent, which leave
//             the same point and read as one line branching. Every loop-back is its own trunk.
//   track     a trunk's position inside a channel. Track 0 is baseX, the rest sit either side.
//             VERTICAL - not a swim lane (`lane` already means that here: see svgLanes).
//   corridor  the room a channel has to spread: parent's stub .. child's arrow. ~33px between
//             adjacent columns, which caps this at about eleven tracks. Past that, geometry cannot
//             help - selecting an event fades everything that does not touch it, and that is the
//             way out.

import positionHelpers from '@/helpers/positionHelpers'
import svgPathHelpers from '@/helpers/svgPathHelpers'

// Gap between two tracks: wide enough to read as two lines.
export const TRACK_SPACING = 8

// Tightest gap we allow. Below this two lines look like one. Only a channel too full to hold
// TRACK_SPACING gets here. Must stay below TRACK_SPACING: raise it to 8 and tracks can never close
// up, so a channel fits 5 of them instead of 11 and the rest stack up again.
export const MIN_TRACK_SPACING = 3

// Flat run kept either side of the vertical, so the corners stay drawable and the arrow keeps a
// straight approach into the child.
const MIN_LEG = 6

export const connectionChannelKey = (parentId, childId) => `${parentId}->${childId}`

// Highest trunk first, so it keeps baseX and later ones fan outwards. That ordering is what stops
// nested connections from crossing, and it is what lets assignTracks get away with one number
// per track.
const topDown = (a, b) => a.yMin - b.yMin || a.yMax - b.yMax || (a.trunkId < b.trunkId ? -1 : 1)

// The vertical a forward connection drops through on its way into the child.
const describeForward = (parentNode, childNode, coordinates) => {
  const { xStart, yStart, xEnd, yEnd } = coordinates
  if (yStart === yEnd) {
    // flat line across one row - no vertical, nothing to overlap
    return null
  }

  return {
    parentId: parentNode.id,
    childId: childNode.id,
    // one trunk per parent: these leave the same point, so they must read as one line that branches
    trunkId: parentNode.id,
    baseX: svgPathHelpers.getForwardChannelX(parentNode.type, coordinates),
    // the corridor's two walls
    xStart,
    xEnd,
    // the rows this vertical covers: two connections collide only if these overlap AND the column matches
    yMin: Math.min(yStart, yEnd),
    yMax: Math.max(yStart, yEnd)
  }
}

// The vertical a loop-back climbs to reach its child - the same x a forward connection into that
// column drops through, so it shares the channel.
//
// Its other two segments (a vertical out of the parent, a horizontal run below the nodes) are not
// handled. They can only collide with other loop-backs, and the horizontal one would need a
// row-keyed twin of this module.
const describeBackward = (parentNode, childNode, coordinates) => {
  const { yStart, xEnd, yEnd, halfWidth } = coordinates

  return {
    parentId: parentNode.id,
    childId: childNode.id,
    // never share a track with this parent's forward connections - those run the other way
    trunkId: `back:${parentNode.id}:${childNode.id}`,
    baseX: svgPathHelpers.getBackwardChannelX(coordinates),
    // The parent is to the RIGHT of the child here, so its stub is no use as a wall. Borrow the wall
    // a forward connection from the previous column would have, which puts both in one corridor.
    xStart: xEnd - 2 * halfWidth,
    xEnd,
    // from the child's row down to the horizontal run beneath the nodes
    yMin: yEnd,
    yMax: Math.max(yStart, yEnd) + 73
  }
}

// One connection, measured - or null if it has no straight vertical and so cannot overlap anything.
// Dropped here: a `start` parent (not a real event), and a connection too short for the forward
// shape, which is drawn as an S-curve.
const describeConnection = (parentNode, childNode) => {
  if (!parentNode) {
    return null
  }
  const coordinates = svgPathHelpers.getConnectionCoordinates(parentNode, childNode)
  const xDistance = childNode.x - parentNode.x
  if (xDistance > svgPathHelpers.getBreakingPoint(parentNode.type, childNode.type)) {
    return describeForward(parentNode, childNode, coordinates)
  }
  if (xDistance <= 0) {
    return describeBackward(parentNode, childNode, coordinates)
  }

  return null
}

// Files a connection under its channel, then under its trunk:
//
//   channels = Map {
//     1 => { anchorX, trunks: Map { 'e6' => [e6->e8, e6->e9], 'e5' => [e5->e7] } },
//     2 => { anchorX, trunks: Map { ... } }
//   }
//
// A gateway drops at 210 and a task at 210.5 - different numbers, one line to the eye. So the channel
// key is the COLUMN, which catches both, and every track in it measures from one anchorX. Key on
// baseX instead and those two stay half a pixel apart, which is still on top of each other.
const addToChannel = (channels, connection) => {
  const { baseX, trunkId, yMin, yMax } = connection

  const slot = positionHelpers.getSlotXFromPoint(baseX)
  let channel = channels.get(slot)
  if (!channel) {
    channel = { anchorX: baseX, trunks: new Map() }
    channels.set(slot, channel)
  } else if (baseX < channel.anchorX) {
    channel.anchorX = baseX
  }

  const trunk = channel.trunks.get(trunkId)
  if (!trunk) {
    channel.trunks.set(trunkId, { trunkId, yMin, yMax, connections: [connection] })

    return
  }
  // A trunk covers all its children - e6->e8 reaches row 328 and e6->e9 reaches 608, so e6's trunk
  // spans 48..608 - and its track holds that whole run, so nothing lands in a gap e6 passes through.
  trunk.yMin = Math.min(trunk.yMin, yMin)
  trunk.yMax = Math.max(trunk.yMax, yMax)
  trunk.connections.push(connection)
}

const collectChannels = (svgGraph) => {
  const channels = new Map()
  for (const childNode of Object.values(svgGraph)) {
    for (const parentId of childNode.parents || []) {
      const connection = describeConnection(svgGraph[parentId], childNode)
      if (connection) {
        addToChannel(channels, connection)
      }
    }
  }

  return channels
}

// Gives each trunk the first free track; returns how many that took.
//
// Trunks arrive top-down, so a track is free exactly when its lowest point is at or above the new
// trunk's top - one number per track, no trunk-against-trunk comparison. Touching counts as free:
// two trunks meeting at a shared event do not hide each other. A trunk that overlaps nothing gets
// track 0 and never moves, which is the common case.
const assignTracks = (trunks) => {
  const trackBottoms = []
  for (const trunk of trunks) {
    let track = trackBottoms.findIndex((bottom) => bottom <= trunk.yMin)
    if (track === -1) {
      track = trackBottoms.length
    }
    trackBottoms[track] = trunk.yMax
    trunk.track = track
  }

  return trackBottoms.length
}

// Half-width of the corridor. Every connection in the channel has to fit, so the tightest one wins.
const measureCorridor = (anchorX, trunks) => {
  let room = Infinity
  for (const trunk of trunks) {
    for (const { xStart, xEnd } of trunk.connections) {
      room = Math.min(room, anchorX - (xStart + MIN_LEG), (xEnd - MIN_LEG) - anchorX)
    }
  }

  return room
}

// How wide the gaps are, and how far out a track may sit.
//
// The corridor is only ~33px, so past five tracks the gaps close up - down to MIN_TRACK_SPACING,
// which reaches about eleven. After that `maxSteps` pins the surplus to the corridor edge, landing
// them exactly on the outermost track. Deliberate: two lines on top of each other is no worse than
// the bug, whereas two a pixel apart read as one thick line and misstate how many there are.
const planFan = (anchorX, trunks, trackCount) => {
  const steps = Math.ceil((trackCount - 1) / 2)
  if (steps === 0) {
    // one track never moves, so its corridor is not worth measuring
    return { spacing: TRACK_SPACING, maxSteps: 0 }
  }
  const room = measureCorridor(anchorX, trunks)
  if (room <= 0) {
    return { spacing: TRACK_SPACING, maxSteps: 0 }
  }
  const spacing = Math.max(MIN_TRACK_SPACING, Math.min(TRACK_SPACING, room / steps))

  return { spacing, maxSteps: Math.max(1, Math.floor(room / spacing)) }
}

// Track number to pixels. Tracks alternate around baseX - 0, -1, +1, -2, +2 - so a busy channel
// stays centred where the reader expects it.
const trackOffset = (track, spacing, maxSteps) => {
  const magnitude = Math.min(Math.ceil(track / 2), maxSteps) * spacing

  return track % 2 === 1 ? -magnitude : magnitude
}

// A track past the corridor's edge would fold the line back over its parent or bury the arrow, so it
// is held at the edge instead.
const clampToCorridor = (targetX, { xStart, xEnd, baseX }) => {
  const min = xStart + MIN_LEG
  const max = xEnd - MIN_LEG
  if (min >= max) {
    return baseX
  }

  return Math.min(Math.max(targetX, min), max)
}

// Maps `parentId->childId` to the pixels its vertical must move to clear the rest of its channel.
//
// Sparse: a connection that does not move is absent, which the caller reads as zero. On a large
// diagram ~98% of connections never overlap anything, and emitting a zero for each would mean
// building thousands of keys to say "stay where you are".
export const buildConnectionChannelOffsets = (svgGraph) => {
  const offsets = {}
  if (!svgGraph) {
    return offsets
  }
  for (const { anchorX, trunks } of collectChannels(svgGraph).values()) {
    const sorted = [...trunks.values()].sort(topDown)
    const { spacing, maxSteps } = planFan(anchorX, sorted, assignTracks(sorted))

    for (const trunk of sorted) {
      const targetX = anchorX + trackOffset(trunk.track, spacing, maxSteps)
      for (const connection of trunk.connections) {
        const offset = clampToCorridor(targetX, connection) - connection.baseX
        if (offset !== 0) {
          offsets[connectionChannelKey(connection.parentId, connection.childId)] = offset
        }
      }
    }
  }

  return offsets
}
